import type { AppNameValue, SupportAccount } from '@my/directory-api';
import { Button, SelectInput } from '@my/ui';
import { ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { getConfig } from '@/configuration/config';
import { appColour } from './lib/appColours';

/**
 * Hand this customer over to one of the operational apps.
 *
 * <p>
 * THE JOIN BETWEEN CONTROL PLANE AND DATA PLANE. My Hub deliberately does not
 * show a customer's orders, invoices or resources — that is what the apps are
 * for, and looking at them there means the red support bar is on screen saying
 * whose data it is. This picks the customer; the app does the work.
 * </p>
 *
 * <p>
 * The session travels as query parameters because the apps are separate
 * origins and cannot read this one's sessionStorage. That is safe on its own:
 * `X-Account-Key` is honoured only for a token already carrying `super_user`,
 * so the link grants nothing to anybody who follows it without one.
 * </p>
 */
export function OpenInAppPanel({ account }: { account: SupportAccount }) {
  const { appUrls } = getConfig();
  const [organisationId, setOrganisationId] = useState(account.organisations[0]?.id ?? '');

  // Only apps the customer can actually open: handing over to a lapsed one
  // lands support on an empty screen with no explanation.
  const live = account.subscriptions.filter((s) => s.isActive).map((s) => s.appKey);
  const launchable = live.filter((app) => Boolean(appUrls?.[app.toLowerCase()]));

  if (live.length === 0) {
    return <p className="text-xs italic text-slate-400">No apps enabled to open.</p>;
  }

  if (launchable.length === 0) {
    return (
      <p className="text-xs italic text-slate-400">
        No app addresses are configured for this environment, so there is nowhere to hand over to.
      </p>
    );
  }

  if (account.organisations.length === 0) {
    return <p className="text-xs italic text-slate-400">No organisation to open.</p>;
  }

  const open = (app: AppNameValue) => {
    const base = appUrls?.[app.toLowerCase()];
    if (!base || !organisationId) return;
    const url = new URL(base);
    url.searchParams.set('support_account', account.id);
    url.searchParams.set('support_org', organisationId);
    // A new tab, so the Hub stays put — support typically comes back here to
    // pick the next customer.
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-3 text-sm">
      <div className="max-w-xs">
        <SelectInput
          label="Organisation"
          options={account.organisations.map((o) => ({ label: o.name, value: o.id }))}
          value={organisationId}
          onChange={(e) => setOrganisationId(e.target.value)}
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {launchable.map((app) => (
          <Button
            key={app}
            variant="secondary"
            rightIcon={ExternalLink}
            onClick={() => open(app)}
            // Same colour the app wears in the list and the subscriptions
            // grid — the point of colouring them at all is that you learn it
            // once. `cn` is tailwind-merge, so this beats the variant.
            className={`border ${appColour(app)} hover:brightness-95`}
          >
            {app}
          </Button>
        ))}
      </div>
    </div>
  );
}
